import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-saas-gain-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="space-y-3">
      <!-- Explication du flux -->
      <div
        class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700 dark:text-blue-300 space-y-1"
      >
        <p class="font-semibold text-blue-800 dark:text-blue-300 text-[13px]">
          Comment se calcule votre benefice ?
        </p>
        <p>
          📦 <strong>Gaspillage</strong> = invendus valorises au prix d'achat
          marchand — reduit votre marge directement
        </p>
        <p>
          📉 <strong>Ruptures</strong> = clients repartis les mains vides —
          ventes manquees estimees a ~{{ industryMissedRate }}% du CA
        </p>
        <p>
          💡 Moins de gaspi + moins de ruptures =
          <strong
            >CA effectif plus eleve → plus de marge → plus de benefice
            net</strong
          >
        </p>
      </div>

      <!-- Tableau comparatif - grille plate pour alignement parfait -->
      <div class="rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 text-xs">
        <!-- En-têtes -->
        <div class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700">
          <div class="bg-red-50 dark:bg-red-900/20 px-4 py-3">
            <div class="font-bold text-red-700 dark:text-red-300 text-sm">
              ❌ Sans recommandations
            </div>
            <div class="text-gray-400 text-[11px]">
              Gestion manuelle estimee
            </div>
          </div>
          <div class="bg-green-50 dark:bg-green-900/20 px-4 py-3">
            <div class="font-bold text-green-700 dark:text-green-300 text-sm">
              ✅ Avec Predivent
            </div>
            <div class="text-gray-400 text-[11px]">Optimise par IA</div>
          </div>
        </div>

        <!-- CA encaissé -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t border-gray-100 dark:border-slate-700"
        >
          <div
            class="bg-red-50 dark:bg-red-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            <span class="text-gray-500 dark:text-gray-400">💵 CA encaisse</span>
            <span class="font-semibold text-gray-800 dark:text-gray-100"
              >{{ totalRevenue() | number: '1.0-0' }} €</span
            >
          </div>
          <div
            class="bg-green-50 dark:bg-green-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            <span class="text-gray-500 dark:text-gray-400">💵 CA encaisse</span>
            <span class="font-semibold text-gray-800 dark:text-gray-100"
              >{{ totalRevenue() | number: '1.0-0' }} €</span
            >
          </div>
        </div>

        <!-- Coût marchand -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t border-gray-100 dark:border-slate-700"
        >
          <div
            class="bg-red-50 dark:bg-red-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            <span class="text-gray-500 dark:text-gray-400"
              >🧭 Coût marchand
              @if (totalRealCost() === null) {
                <span class="text-[10px] text-gray-400"
                  >(~{{ 100 - netRate() }}% CA)</span
                >
              }
            </span>
            <span class="font-semibold text-orange-500"
              >-{{ estimatedCostSans | number: '1.0-0' }} €</span
            >
          </div>
          <div
            class="bg-green-50 dark:bg-green-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            @if (totalRealCost() !== null) {
              <span class="text-gray-500 dark:text-gray-400">🧭 Coût marchand</span>
              <span class="font-semibold text-orange-500"
                >-{{ totalRealCost() | number: '1.0-0' }} €</span
              >
            } @else {
              <span class="text-gray-300 italic">Coût non renseigné</span>
              <span class="text-gray-300">—</span>
            }
          </div>
        </div>

        <!-- Gaspillage -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t border-gray-100 dark:border-slate-700"
        >
          <div
            class="bg-red-50 dark:bg-red-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            <span class="text-gray-500 dark:text-gray-400"
              >🗑️ Gaspillage
              <span class="text-[10px] text-gray-400"
                >(~{{ industryWasteRate }}% coût)</span
              >
            </span>
            <span class="font-semibold text-red-600 dark:text-red-400"
              >-{{ estimatedWasteWithout | number: '1.0-0' }} €</span
            >
          </div>
          <div class="bg-green-50 dark:bg-green-900/20 px-4 py-1.5 space-y-0.5">
            <div class="flex justify-between items-baseline">
              <span class="text-gray-500 dark:text-gray-400">🗑️ Gaspillage</span>
              <span
                class="font-semibold"
                [class]="
                  totalWaste() > 0 ? 'text-orange-500' : 'text-green-600 dark:text-green-400'
                "
              >
                {{ totalWaste() > 0 ? '-' : ''
                }}{{ totalWaste() | number: '1.0-0' }} €
              </span>
            </div>
            @if (wasteGain > 0) {
              <div class="flex justify-end">
                <span
                  class="text-[10px] bg-green-500 text-white px-2 py-0.5 rounded-full"
                >
                  💧 Eco +{{ wasteGain | number: '1.0-0' }} € vs sans outil
                </span>
              </div>
            }
          </div>
        </div>

        <!-- CA effectif - ligne alignée -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t border-gray-200 dark:border-slate-700"
        >
          <div
            class="bg-red-100 dark:bg-red-900/30/70 flex justify-between items-center px-4 py-2"
          >
            <span class="font-semibold text-gray-700 dark:text-gray-300">CA effectif</span>
            <span class="font-bold text-gray-900 dark:text-white"
              >{{ caEffectifSans | number: '1.0-0' }} €</span
            >
          </div>
          <div
            class="bg-green-100 dark:bg-green-900/30/70 flex justify-between items-center px-4 py-2"
          >
            <span class="font-semibold text-gray-700 dark:text-gray-300">CA effectif</span>
            <div class="flex items-center gap-1.5">
              <span class="font-bold text-green-800 dark:text-green-300"
                >{{ caEffectifAvec | number: '1.0-0' }} €</span
              >
              @if (deltaCA > 0) {
                <span
                  class="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold"
                  >+{{ deltaCA | number: '1.0-0' }} €</span
                >
              }
            </div>
          </div>
        </div>

        <!-- Charges fixes estimées -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t border-gray-100 dark:border-slate-700"
        >
          <div
            class="bg-red-50 dark:bg-red-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            <span class="text-gray-500 dark:text-gray-400"
              >🏗️ Charges fixes estimées (~{{ fixedCostsRate() }}%)</span
            >
            <span class="font-semibold text-red-400"
              >-{{ fixedCostsSans | number: '1.0-0' }} €</span
            >
          </div>
          <div
            class="bg-green-50 dark:bg-green-900/20 flex justify-between items-baseline px-4 py-1.5"
          >
            <span class="text-gray-500 dark:text-gray-400"
              >🏗️ Charges fixes estimées (~{{ fixedCostsRate() }}%)</span
            >
            <span class="font-semibold text-red-400"
              >-{{ fixedCostsAvec | number: '1.0-0' }} €</span
            >
          </div>
        </div>

        <!-- Bénéfice net estimé - ligne alignée -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t-2 border-gray-300 dark:border-slate-600"
        >
          <div
            class="bg-red-200/60 flex justify-between items-center px-4 py-2"
          >
            <span class="font-bold text-gray-700 dark:text-gray-300">Bénéfice net estimé</span>
            <span class="font-bold text-red-700 dark:text-red-300"
              >{{ netProfitSans | number: '1.0-0' }} €</span
            >
          </div>
          <div
            class="bg-green-200/60 flex justify-between items-center px-4 py-2"
          >
            <span class="font-bold text-gray-800 dark:text-gray-100">Bénéfice net estimé</span>
            <div class="flex items-center gap-1.5">
              <span class="font-bold text-green-800 dark:text-green-300 text-[14px]"
                >{{ netProfitAvec | number: '1.0-0' }} €</span
              >
              @if (deltaNetProfit > 0) {
                <span
                  class="text-[11px] bg-green-600 text-white px-2 py-0.5 rounded-full font-bold"
                  >+{{ deltaNetProfit | number: '1.0-0' }} €</span
                >
              }
            </div>
          </div>
        </div>

        <!-- Temps + Ventes manquées -->
        <div
          class="grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700 border-t border-gray-200 dark:border-slate-700"
        >
          <div class="bg-red-50 dark:bg-red-900/20 px-4 py-2 space-y-1.5">
            <div class="flex justify-between items-baseline">
              <span class="text-gray-500 dark:text-gray-400">⏱️ Temps gestion</span>
              <span class="font-semibold text-gray-600 dark:text-gray-400">~2h/jour</span>
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-500 dark:text-gray-400"
                >📉 Ventes manquees
                <span class="text-[10px] text-gray-400"
                  >(~{{ industryMissedRate }}%)</span
                >
              </span>
              <span class="font-semibold text-red-500"
                >-{{ estimatedMissedRevenue | number: '1.0-0' }} €</span
              >
            </div>
          </div>
          <div class="bg-green-50 dark:bg-green-900/20 px-4 py-2 space-y-1.5">
            <div class="flex justify-between items-baseline">
              <span class="text-gray-500 dark:text-gray-400">⚡ Temps gagne</span>
              <span class="font-semibold text-green-600 dark:text-green-400">~2h/jour</span>
            </div>
            <div class="space-y-0.5">
              <div class="flex justify-between items-baseline">
                <span class="text-gray-500 dark:text-gray-400"
                  >📉 Ventes manquees
                  <span class="text-[10px] text-gray-400">
                    @if (totalProducts() > 0) {
                      ({{ totalRuptures() }}/{{ totalProducts() }})
                    } @else {
                      (est.)
                    }
                  </span>
                </span>
                <span class="font-semibold text-orange-500"
                  >-{{ actualMissedRevenue | number: '1.0-0' }} €</span
                >
              </div>
              @if (deltaMissedRevenue > 0) {
                <div class="flex justify-end">
                  <span
                    class="text-[10px] bg-green-500 text-white px-2 py-0.5 rounded-full"
                  >
                    ✨ Réduction +{{ deltaMissedRevenue | number: '1.0-0' }} €
                    vs sans outil
                  </span>
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Avertissement charges fixes -->
      <div
        class="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-xs text-amber-700 dark:text-amber-300"
      >
        <span class="text-base mt-0.5">⚠️</span>
        <div>
          <p class="font-semibold">
            Charges fixes non incluses dans les calculs de marge
          </p>
          <p class="text-amber-600 mt-0.5">
            Carburant · Location emplacement · Amortissement materiel ·
            Assurances
          </p>
          <p class="text-amber-500 mt-0.5 italic">
            Estimation utilisee : ~{{ fixedCostsRate() }}% du CA — a ajuster
            selon votre situation
          </p>
        </div>
      </div>
    </div>
  `,
})
export class SaasGainCardComponent {
  readonly totalRevenue = input<number>(0);
  readonly totalWaste = input<number>(0);
  readonly wasteRate = input<number>(0);
  readonly netProfit = input<number>(0);
  readonly netRate = input<number>(15);
  readonly realProfitRate = input<number | null>(null);
  readonly totalRealCost = input<number | null>(null);
  readonly totalRuptures = input<number>(0);
  readonly totalProducts = input<number>(0);
  readonly avgAccuracy = input<number | null>(null);
  readonly fixedCostsRate = input<number>(15);

  get displayNetRate(): number {
    return this.realProfitRate() ?? this.netRate();
  }

  get isRealRate(): boolean {
    return this.realProfitRate() !== null;
  }

  readonly industryWasteRate = 15;
  readonly industryMissedRate = 7;

  get estimatedWasteWithout(): number {
    const base =
      this.totalRealCost() ??
      Math.round(this.totalRevenue() * (1 - this.netRate() / 100));
    return Math.round((base * this.industryWasteRate) / 100);
  }

  /** SANS outil : taux industrie fixe */
  get estimatedMissedRevenue(): number {
    return Math.round((this.totalRevenue() * this.industryMissedRate) / 100);
  }

  /** AVEC outil : basé sur les ruptures réelles. Fallback = même que SANS si pas de données. */
  get actualMissedRevenue(): number {
    const plafond = Math.round(
      (this.totalRevenue() * this.industryMissedRate) / 100,
    );
    if (this.totalProducts() <= 0) return plafond;
    const ratio = Math.min(1, this.totalRuptures() / this.totalProducts());
    return Math.round(plafond * ratio);
  }

  get deltaMissedRevenue(): number {
    return Math.max(0, this.estimatedMissedRevenue - this.actualMissedRevenue);
  }

  get estimatedCostSans(): number {
    if (this.totalRealCost() !== null) return this.totalRealCost()!;
    return Math.round(this.totalRevenue() * (1 - this.netRate() / 100));
  }

  get caEffectifSans(): number {
    return Math.max(
      0,
      this.totalRevenue() - this.estimatedCostSans - this.estimatedWasteWithout,
    );
  }

  get caEffectifAvec(): number {
    const cost = this.totalRealCost() ?? 0;
    return Math.max(0, this.totalRevenue() - cost - this.totalWaste());
  }

  get deltaCA(): number {
    return Math.max(0, this.caEffectifAvec - this.caEffectifSans);
  }

  get beneficeSans(): number {
    return this.caEffectifSans; // cost already deducted
  }

  get fixedCostsSans(): number {
    return Math.round(this.caEffectifSans * (this.fixedCostsRate() / 100));
  }

  get fixedCostsAvec(): number {
    return Math.round(this.caEffectifAvec * (this.fixedCostsRate() / 100));
  }

  get netProfitSans(): number {
    return Math.max(0, this.caEffectifSans - this.fixedCostsSans);
  }

  get netProfitAvec(): number {
    return Math.max(0, this.caEffectifAvec - this.fixedCostsAvec);
  }

  get deltaNetProfit(): number {
    return Math.max(0, this.netProfitAvec - this.netProfitSans);
  }

  get wasteGain(): number {
    return Math.max(0, this.estimatedWasteWithout - this.totalWaste());
  }
}
